/* eslint-disable @next/next/no-img-element */
import { useState, useEffect, useCallback, memo } from 'react'
import NextImage, { ImageProps as NextImageProps } from 'next/image'
import axios from 'axios'

type ImageProps = NextImageProps & {
  src: string
  alt: string
  className?: string
  fallbackSrc?: string
  loadingSrc?: string
}

function Image({ src, alt, className, fallbackSrc, loadingSrc, ...props }: ImageProps) {
  const imageIsLocal = src.startsWith('/img/')
  const imageIsCached = ['images.nf.domains', 'algoxnft.mypinata.cloud'].some((domain) =>
    src.includes(domain)
  )

  const [image, setImage] = useState({
    src: imageIsLocal ? src : loadingSrc || `/img/nfd-loading-placeholder.jpg`,
    isUnoptimized: false
  })

  const handleError = useCallback(() => {
    setImage({
      src: fallbackSrc || `/img/nfd-loading-placeholder_error.jpg`,
      isUnoptimized: false
    })
  }, [fallbackSrc])

  useEffect(() => {
    if (!src || imageIsLocal) {
      return
    }

    const prefetchIpfsImages = async (CID: string) => {
      try {
        const response = await axios.head(`https://images.nf.domains/ipfs/${CID}`)

        if (response.status === 200 && response.config.url) {
          setImage({
            src: response.config.url,
            isUnoptimized: false
          })

          return
        }
      } catch (e) {
        console.info(CID, 'is not cached, trying IPFS gateways...')
      }

      // More fallback gateways can be added here
      const gateways = [`https://ipfs.io/ipfs/${CID}`, `https://cloudflare-ipfs.com/ipfs/${CID}`]
      const prefetch = (src: string) => axios.head(src)

      try {
        const result = await Promise.any(gateways.map((url) => prefetch(url)))
        setImage({ src: result.config.url as string, isUnoptimized: false })
      } catch (e) {
        handleError()
      }
    }

    if (src.startsWith('ipfs://')) {
      const CID = src.split('ipfs://')[1]
      prefetchIpfsImages(CID)
    } else {
      setImage({
        src,
        isUnoptimized: !imageIsCached
      })
    }
  }, [src, imageIsLocal, imageIsCached, handleError])

  return (
    <NextImage
      src={image.src}
      alt={alt}
      className={className}
      unoptimized={image.isUnoptimized}
      onError={handleError}
      {...props}
    />
  )
}

export default memo(Image)
